import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectIntegrationId } from '@/redux/session/selectors';
import { WhatsAppIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { supportBREmail, supportEmail } from '@/util/constants';
import type { WhatsappConflict } from '@/util/facebookSdk';

import { DeleteFieldset } from '../Components/DeleteFieldset';

interface Props {
  onOAuthClick: () => void;
  isConnecting?: boolean;
  conflict?: WhatsappConflict;
  pin: string;
  onPinChange: (pin: string) => void;
}

const PIN_PATTERN = /^\d{6}$/;

const WhatsappConnect = ({ onOAuthClick, isConnecting, conflict, pin, onPinChange }: Props) => {
  const { t, i18n } = useTranslation();
  const deskId = useDeskId();
  const integrationId = useSelector(selectIntegrationId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const { integration } = useIntegrations<WhatsAppIntegration>(deskId, integrationId);
  const supportContactEmail = i18n.language === 'pt-br' ? supportBREmail : supportEmail;
  const isPinInvalid = pin !== '' && !PIN_PATTERN.test(pin);

  if (!integration) {
    return null;
  }

  return (
    <div className="flex w-xl flex-col gap-4">
      <Card className="rounded-md py-0 shadow-none">
        <CardContent className="flex flex-col gap-4 px-4 pt-4 pb-5">
          <div className="flex flex-col gap-1">
            <p className="text-sm leading-none font-medium">
              {t('integrations.whatsapp.connect.title')}
            </p>
            <p className="text-sm leading-5 text-foreground-muted">
              {t('integrations.whatsapp.connect.subtitle')}
            </p>
          </div>
          {conflict && (
            <Alert variant="destructive">
              <AlertDescription>
                {conflict.kind === 'with-access' ? (
                  <>
                    <span>{t('errors.whatsapp_already_attached_with_access')}</span>
                    <Button asChild variant="outline-neutral" size="sm">
                      <Link
                        to={`/${conflict.slug}/environments/${conflict.deskId}/integrations/${conflict.integrationId}`}
                      >
                        {t('integrations.whatsapp.connect.open_existing')}
                      </Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <span>{t('errors.whatsapp_already_attached_no_access')}</span>
                    <span>
                      {t('errors.whatsapp_already_attached_contact_support')}{' '}
                      <Button asChild variant="link-secondary" size="sm" className="h-auto px-0">
                        <a href={`mailto:${supportContactEmail}`}>{supportContactEmail}</a>
                      </Button>
                    </span>
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="whatsapp-pin">{t('integrations.whatsapp.pin.label')}</Label>
            <Input
              id="whatsapp-pin"
              inputMode="numeric"
              maxLength={6}
              autoComplete="off"
              value={pin}
              onChange={(e) => onPinChange(e.target.value)}
              aria-invalid={isPinInvalid}
              aria-describedby="whatsapp-pin-hint"
              disabled={!canWrite}
            />
            {isPinInvalid ? (
              <p id="whatsapp-pin-hint" role="alert" className="text-sm text-warning">
                {t('errors.whatsapp_invalid_pin')}
              </p>
            ) : (
              <p id="whatsapp-pin-hint" className="text-xs text-foreground-muted">
                {t('integrations.whatsapp.pin.helper')}
              </p>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={onOAuthClick}
              isLoading={isConnecting}
              disabled={!canWrite || !!conflict || isPinInvalid}
            >
              {t('integrations.connect')}
            </Button>
          </div>
        </CardContent>
      </Card>
      {canWrite && (
        <div className="flex justify-start">
          <DeleteFieldset integration={integration} />
        </div>
      )}
    </div>
  );
};

export default WhatsappConnect;
